/** Apenas dígitos */
export function onlyDigits(s: string): string {
  return s.replace(/\D/g, '');
}

/** CPF/CNPJ com máscara conforme o usuário digita (BR). */
export function formatDocumentoBr(raw: string): string {
  const d = onlyDigits(raw).slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return d
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

/** Exibição de documento em listagens (recebe valor já normalizado do backend). */
export function formatDocumentoExibicao(stored?: string | null): string {
  if (!stored) return '—';
  return formatDocumentoBr(onlyDigits(stored));
}

/** Telefone BR (celular/fixo) com máscara. */
export function formatTelefoneBr(raw: string): string {
  const d = onlyDigits(raw).slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** CEP com máscara: 00000-000 */
export function formatCepBr(raw: string): string {
  const d = onlyDigits(raw).slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/** Placa Mercosul ou antiga: maiúsculas, só letras e números, máx. 7. */
export function formatPlacaInput(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 7);
}

/** Moeda BRL para exibição (2 casas). */
export function formatMoedaBrl(valor: number): string {
  if (Number.isNaN(valor)) return '';
  return valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Digitação estilo caixa (apenas dígitos) → valor em reais.
 * Ex.: dígitos "4590" → 45,90 R$.
 */
export function reaisAPartirDeDigitosMoeda(digits: string): number {
  const d = onlyDigits(digits).slice(0, 12);
  if (!d) return 0;
  return parseInt(d, 10) / 100;
}

/** String de dígitos a partir do valor em reais (para reabrir o diálogo). */
export function digitosMoedaAPartirDeReais(reais: number): string {
  if (!Number.isFinite(reais) || reais < 0) return '';
  return String(Math.round(reais * 100));
}

/** Km inteiro com separador de milhar só para exibição. */
export function formatKmExibicao(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '';
  return Math.round(n).toLocaleString('pt-BR');
}

/**
 * Campo só-dia (`@db.Date` / ISO à meia-noite UTC). Formata o calendário
 * gravado, sem recuar um dia no fuso de Brasília.
 */
export function formatarDataCalendario(iso: string | Date | null | undefined): string {
  if (iso == null || iso === '') return '—';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

/**
 * Diferença em dias entre o calendário local de hoje e uma data só-dia
 * gravada em UTC. Positivo = atrasado.
 */
export function diasAtrasoCalendario(vencimentoIso: string): number {
  const v = new Date(vencimentoIso);
  if (Number.isNaN(v.getTime())) return 0;
  const hoje = new Date();
  const utcVenc = Date.UTC(v.getUTCFullYear(), v.getUTCMonth(), v.getUTCDate());
  const utcHoje = Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  return Math.round((utcHoje - utcVenc) / 86_400_000);
}

/** Soma meses a um `YYYY-MM-DD` (input type=date) sem recuar um dia no fuso local. */
export function adicionarMesesCalendario(isoYmd: string, meses: number): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoYmd.slice(0, 10));
  if (!m) return new Date(NaN);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1 + meses, Number(m[3])));
}

export { filialLabel } from './filial-label';
